/* eslint-disable max-lines */
import { PROJECT_STATUS } from '@linearb/linjs-common';
import _ from 'lodash';
import {
  PROJECTS_VIEW_TYPES,
  PROJECT_EMPTY_STATES,
  PROJECT_EMPTY_STATES_KEYS,
  DELIVERY_STATES_API_KEYS
} from './projects.consts';
import ProjectColumnEmptyState from './components/ProjectColumnEmptyState';
import ProjectColumn from './components/ProjectColumn';
import PeopleEffortColumn from './components/PeopleEffortColumn';
import InvestmentBreakdown from './components/InvestmentBreakdown';
import DeliveryColumn from './components/DeliveryColumn';
import DeliveryBreakdown from './components/DeliveryBreakdown';
import PeopleEffortExtendedColumn from './components/PeopleEffortExtendedColumn';
import PeopleEffortOverTimeColumn from './components/PeopleEffortOverTimeColumn';
import DeliveryTypeColumn from './components/DeliveryTypeColumn';
import DeliveryTypeChartColumn from './components/DeliveryTypeChartColumn';
import IssuesTypesToggle from './components/IssuesTypesToggle';
import AccuracyColumn from './components/AccuracyColumn';
import MenuButton from './components/MenuButton';
import { getDeliveryData } from './projects.builder.common';
import AppRouter from 'common/AppRouter';
import {
  getChartsData,
  getTotalsFromIssuesData
} from './services/projectExtended.services';
import { Flex } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';

import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import ProjectColumnSkeleton from './components/ProjectColumnSkeleton';

export const withProject = (
  row,
  {
    project,
    isLastIteration,
    iterations,
    addProjectToFavorites,
    removeProjectFromFavorites,
    isFavoriteProject
  }
) => {
  const isNewProjectLoading = project.status === PROJECT_STATUS.NEW;
  const isProjectUpdating = project.status === PROJECT_STATUS.EDITED;

  row.projects = isHovered => (
    <ProjectColumn
      isLoading={isProjectUpdating}
      projectState={{}}
      isNewProjectLoading={isNewProjectLoading}
      project={project}
      showFavoritesIcon={
        !isNewProjectLoading && (isHovered || isFavoriteProject(project))
      }
      addProjectToFavorites={addProjectToFavorites}
      removeProjectFromFavorites={removeProjectFromFavorites}
      isFavoriteProject={isFavoriteProject}
      {...(isLastIteration
        ? {
            lastIteration: iterations && iterations[0]
          }
        : {})}
    />
  );
};

export const withPeopleEffort = (
  row,
  { project, projectConfig, isLastIteration }
) => {
  row.people_effort =
    project.avg_contributors || project.fte ? (
      <PeopleEffortColumn
        projectConfig={projectConfig}
        project={project}
        isLastIteration={isLastIteration}
      />
    ) : (
      <ProjectColumnEmptyState
        {...PROJECT_EMPTY_STATES[PROJECT_EMPTY_STATES_KEYS.noPeopleEffort]}
      />
    );
};

export const withInvestmentBreakdown = (
  row,
  { investmentData = {}, noInvestmentData, investmentColorsMap, pmViewer }
) => {
  const investmentDataArr = Object.keys(investmentData || {}).map(key => {
    return { category: key, value: Math.round(investmentData[key]) };
  });

  row.issues_breakdown = noInvestmentData ? (
    <ProjectColumnEmptyState
      {...PROJECT_EMPTY_STATES[PROJECT_EMPTY_STATES_KEYS.noIssueTypesData](
        pmViewer
      )}
    />
  ) : (
    <InvestmentBreakdown
      displayType={PROJECTS_VIEW_TYPES.PROJECTS_VIEW}
      investmentSummary={investmentDataArr}
      investmentColorsMap={investmentColorsMap}
    />
  );
};

export const withDelivery = (
  row,
  { project, issuesChartConfig, isLastIteration, deliveryType }
) => {
  const isScrumProject = _.get(project, 'cadence.type') === 'Scrum';
  const issuesData = getChartsData({
    project,
    totalCountsKey: 'total_planning_counts',
    totalStoryPointsKey: 'total_planning_story_points',
    countsKey: 'planning_counts',
    storyPointsKey: 'planning_story_points',
    isScrumProject,
    calculationKey: deliveryType
  });
  const issuesDataSorted = issuesChartConfig.issuesSorted({
    issuesData
  });
  const totals = getTotalsFromIssuesData(issuesData);

  const deliveryData = getDeliveryData({
    project,
    isScrumProject,
    deliveryType,
    totals
  });

  row.issues_over_time = (
    <DeliveryColumn
      issuesData={issuesDataSorted}
      displayType={PROJECTS_VIEW_TYPES.PROJECTS_VIEW}
      project={project}
      deliveryType={deliveryType}
      chartConfig={issuesChartConfig}
      isLastIteration={isLastIteration}
      deliveryData={deliveryData}
    />
  );
};

export const WithProjectExtended = (
  row,
  { project, selectedPeriod, selectedTimeRange }
) => {
  const startDate = _.get(selectedTimeRange, 'start_date');
  const endDate = _.get(selectedTimeRange, 'end_date');
  row.openProjectExtended = history => {
    history.push({
      pathname: `${AppRouter.PROJECTS_API.path}/${project.id}`,
      search: `?duration_filter=${selectedPeriod}&start_date=${startDate}&end_date=${endDate}`
    });
  };
};

export const withNoProjectExtended = row => {
  row.openProjectExtended = () => {
    _.noop();
  };
};

export const withDefaultCursor = row => {
  row.styles = { cursor: 'default' };
};

export const withMenuButton = (
  row,
  {
    isCanConfigProjects,
    project,
    onDeleteProject,
    pmSensorAuthId,
    toggleModal,
    toggleSnackBar,
    onSaveClick
  }
) => {
  row.menu_button = isCanConfigProjects ? (
    <MenuButton
      project={project}
      onDeleteProject={onDeleteProject}
      pmSensorAuthId={pmSensorAuthId}
      toggleModal={toggleModal}
      onSaveClick={onSaveClick}
      toggleSnackBar={toggleSnackBar}
    />
  ) : null;
};

export const withNewProject = (row, { project }) => {
  row.projects = <ProjectColumn isNewProjectLoading={true} project={project} />;
};

export const withNewProjectNoData = row => {
  row.people_effort = (
    <ProjectColumnEmptyState
      {...PROJECT_EMPTY_STATES[PROJECT_EMPTY_STATES_KEYS.newProjectLoading]}
    />
  );
};

export const withExtendedPeopleEffort = ({
  peopleEffort,
  peopleEffortAvg,
  uniquePeopleEffort,
  uniqueExcludedPeople,
  projectConfig,
  project,
  totalPeople,
  selectedPeriod,
  isSingleIterationView,
  iterations,
  singleIteration,
  isScrumProject,
  isFTECalculation
}) => {
  return {
    type: 'People Effort',
    icon: 'TeamsPeopleIcon',
    total: isScrumProject ? `(${peopleEffort?.length} project members)` : null,
    chartTitle: 'People effort over time',
    emptyStateKey:
      peopleEffort?.length === 0
        ? PROJECT_EMPTY_STATES_KEYS.noPeopleEffort
        : false,
    pieComponent: (
      <PeopleEffortExtendedColumn
        projectConfig={projectConfig}
        project={project}
        peopleEffortAvg={peopleEffortAvg}
        uniquePeopleEffort={uniquePeopleEffort}
        uniqueExcludedPeople={uniqueExcludedPeople}
        totalNumberOfPeople={totalPeople}
        selectedPeriod={selectedPeriod}
        isSingleIterationView={isSingleIterationView}
        iterations={iterations}
        iterationsLength={iterations.length}
        singleIteration={singleIteration}
        isFTECalculation={isFTECalculation}
      />
    ),
    chartComponent: (
      <PeopleEffortOverTimeColumn
        iterations={iterations}
        isExtendedView={true}
      />
    )
  };
};

export const WithExtendedInvestmentProfile = ({
  investmentData,
  investmentColorsMap,
  project,
  projectEstimationFields,
  projectsEstimationFields,
  setProjectsEstimationFields
}) => {
  const investmentDataPerType = investmentData;

  const noInvestmentData = _.every(
    investmentDataPerType.flatMap(({ value }) => value),
    _.partial(_.isEqual, 0)
  );

  return {
    type: 'Investment Profile',
    icon: 'InvestmentProfile',
    chartTitle: 'Investment profile over time',
    filter: (
      <IssuesTypesToggle
        project={project}
        issueType={'investment_type'}
        projectEstimationFields={projectEstimationFields}
        projectsEstimationFields={projectsEstimationFields}
        setProjectsEstimationFields={setProjectsEstimationFields}
      />
    ),
    emptyStateKey:
      !!noInvestmentData && PROJECT_EMPTY_STATES_KEYS.noIssueTypesData,
    pieComponent: (
      <InvestmentBreakdown
        displayType={PROJECTS_VIEW_TYPES.EXTENDED_PIE_VIEW}
        investmentSummary={investmentDataPerType}
        investmentColorsMap={investmentColorsMap}
      />
    ),
    chartComponent: (
      <InvestmentBreakdown
        displayType={PROJECTS_VIEW_TYPES.EXTENDED_CHART_VIEW}
        investmentSummary={investmentDataPerType}
        investmentColorsMap={investmentColorsMap}
      />
    )
  };
};

export const WithExtendedDelivery = ({
  issuesData,
  totals,
  projectConfig,
  issuesChartConfig,
  issuesChartType,
  project,
  projectsExcludedIssues,
  setProjectsExcludedIssues,
  selectedDeliveryState,
  setSelectedDeliveryState,
  isLastIteration,
  isSingleIterationView,
  projectEstimationFields,
  projectsEstimationFields,
  setProjectsEstimationFields,
  isScrumProject,
  noProjectData,
  selectedPeriod,
  isPulse
}) => {
  const issuesDataSorted = issuesChartConfig.issuesSorted({
    issuesData
  });

  const { icon } = projectConfig;
  const {
    [DELIVERY_STATES_API_KEYS.UNCOMPLETED]: uncompleted,
    [DELIVERY_STATES_API_KEYS.COMPLETED]: completed,
    [DELIVERY_STATES_API_KEYS.ADDED]: added,
    [DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED]: added_uncompleted
  } = totals;

  const totalWork = Math.round(
    completed + added + uncompleted + added_uncompleted
  );

  return {
    type: 'Delivery',
    icon,
    total: isScrumProject ? `(${totalWork} total work)` : null,
    chartTitle: `${issuesChartConfig.title}`,
    filter: (
      <IssuesTypesToggle
        project={project}
        issueType={'delivery_type'}
        projectEstimationFields={projectEstimationFields}
        projectsEstimationFields={projectsEstimationFields}
        setProjectsEstimationFields={setProjectsEstimationFields}
      />
    ),
    emptyStateKey: noProjectData
      ? PROJECT_EMPTY_STATES_KEYS.noDeliveryData
      : false,
    pieComponent: isScrumProject ? (
      <DeliveryBreakdown
        project={project}
        totals={totals}
        issuesData={issuesData}
        selectedDeliveryState={selectedDeliveryState}
        setSelectedDeliveryState={setSelectedDeliveryState}
        isSingleIterationView={isSingleIterationView}
        deliveryType={projectEstimationFields.delivery_type}
        displayType={PROJECTS_VIEW_TYPES.EXTENDED_PIE_VIEW}
        totalWork={totalWork}
        selectedPeriod={selectedPeriod}
        isPulse={isPulse}
      />
    ) : (
      <DeliveryColumn
        displayType={PROJECTS_VIEW_TYPES.EXTENDED_PIE_VIEW}
        type={issuesChartType}
        issuesData={issuesDataSorted}
        project={project}
        projectConfig={projectConfig}
        chartConfig={issuesChartConfig}
        projectsExcludedIssues={projectsExcludedIssues}
        setProjectsExcludedIssues={setProjectsExcludedIssues}
        selectedDeliveryState={selectedDeliveryState}
        setSelectedDeliveryState={setSelectedDeliveryState}
        isSingleIterationView={isSingleIterationView}
        isScrumProject={isScrumProject}
      />
    ),
    chartComponent: (
      <DeliveryColumn
        displayType={PROJECTS_VIEW_TYPES.EXTENDED_CHART_VIEW}
        type={issuesChartType}
        issuesData={issuesDataSorted}
        project={project}
        chartConfig={issuesChartConfig}
        isLastIteration={isLastIteration}
        isScrumProject={isScrumProject}
        projectsExcludedIssues={isScrumProject ? {} : projectsExcludedIssues}
      />
    )
  };
};

export const WithExtendedDeliveryType = ({
  isScrumProject,
  deliveryData,
  noProjectData
}) => {
  const { icon, chartTitle, title } = deliveryData[0];

  return {
    type: title,
    icon,
    chartTitle: `${chartTitle} over time`,
    emptyStateKey: noProjectData
      ? PROJECT_EMPTY_STATES_KEYS.noDeliveryData
      : false,
    pieComponent: (
      <DeliveryTypeColumn
        lineChartData={deliveryData[0]}
        showVelocityPie={isScrumProject}
      />
    ),
    chartComponent: (
      <DeliveryTypeChartColumn
        lineChartData={deliveryData}
        isMax100Percent={isScrumProject}
      />
    )
  };
};

export const WithExtendedAccuracy = ({
  totals,
  deliveryData,
  noProjectData
}) => {
  return {
    type: 'Accuracy Scores',
    icon: 'Accuracy',
    chartTitle: (
      <Flex>
        <div>Accuracy Scores over time</div>
        {deliveryData.map(({ title, color, graphIcon }) => {
          return (
            <Flex items='center' pl='normal' key={title}>
              <Icon name={graphIcon} color={color} />
              <Text pl='0.3rem'>{title}</Text>
            </Flex>
          );
        })}
      </Flex>
    ),
    emptyStateKey: noProjectData
      ? PROJECT_EMPTY_STATES_KEYS.noDeliveryData
      : false,
    pieComponent: (
      <AccuracyColumn lineChartData={deliveryData} totals={totals} />
    ),
    chartComponent: (
      <DeliveryTypeChartColumn
        lineChartData={deliveryData}
        isMax100Percent={false}
      />
    )
  };
};

export const withProjectNoData = (row, { selectedPeriod }) => {
  row.people_effort = (
    <ProjectColumnEmptyState
      {...PROJECT_EMPTY_STATES[PROJECT_EMPTY_STATES_KEYS.noProjectData](
        selectedPeriod
      )}
    />
  );
};

export const withLoadingProject = (row, { type, withoutAnimation } = {}) => {
  const iconName = type === METHOD_NAMES.SCRUM.type ? 'Accuracy' : 'Speed';
  row.people_effort = (
    <ProjectColumnSkeleton
      circle
      lines={[{ width: 30 }, { width: 100 }]}
      withoutAnimation={withoutAnimation}
    />
  );
  row.issues_breakdown = (
    <ProjectColumnSkeleton
      circle
      lines={[{ width: 30 }, { width: 30 }]}
      withoutAnimation={withoutAnimation}
    />
  );

  row.issues_over_time = (
    <ProjectColumnSkeleton
      Icon={<Icon name={iconName} color='catskillWhite' size='xx-large' />}
      lines={[{ width: 100 }, { width: 100 }]}
      withoutAnimation={withoutAnimation}
    />
  );
};
